import { Component, OnDestroy, OnInit } from '@angular/core';
import { UtilSubdomain } from '@fitscovery/partner-web/shared/utils';
import { Partner, PartnerState } from '@fitscovery/partner/data-access';
import { GetProgramsByPartnerIdWithPaging, GetWorkoutsBySubdomainWithPaging, Program, ProgramState, Workout, WorkoutState, GetWorkoutsBySubdomain, GetProgramsByPartnerId } from '@fitscovery/workouts/data-access';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { MetaTagService, PageTitleService } from '@fitscovery/partner-web/shared/services';
import { Metadata } from '@fitscovery/partner-web/shared/models';
import { take, takeUntil } from 'rxjs/operators';
import * as _ from 'lodash';
import { SubscriptionStore } from '@fitscovery/common/classes/subscription-store';

@Component({
  selector: 'fitscovery-feature-workout-list',
  templateUrl: './feature-workout-list.component.html',
  styleUrls: ['./feature-workout-list.component.scss']
})
export class FeatureWorkoutListComponent extends SubscriptionStore implements OnInit, OnDestroy {
  @Select(WorkoutState.workout) workout$: Observable<Workout[] | null>;
  @Select(ProgramState.program) program$: Observable<Program[] | null>;
  @Select(PartnerState.partner) partnerState$: Observable<Partner | null>;
  workoutData: Workout[];
  programData: Program[];
  workoutPageNumber: number;
  programPageNumber: number;
  pageSize: number;
  throttle = 300;
  scrollDistance = 0.2;
  partner: Partner;

  constructor(
    private store: Store,
    private metaTagService: MetaTagService,
    private pageTitleService: PageTitleService,
    private subdomain: UtilSubdomain
  ) {
    super();
    this.workoutPageNumber = 1;
    this.programPageNumber = 1;

    this.pageSize = 10;
  }

  ngOnInit(): void {
    this.workoutData = [];
    this.programData = [];
    
    this.partnerState$.pipe(take(1)).subscribe((partner: Partner | null) => {
      if (partner) {
        this.getPartner();
        this.getWorkoutsBySubdomain();
        this.getProgramsByPartnerId();
        this.setMetatag();
        this.pageTitleService.setPageTitle();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroySubscription();
  }

  onWorkoutScrollEnd() {
    if (this.workoutPageNumber * this.pageSize === this.workoutData.length) {
      this.workoutPageNumber += 1;
      this.getWorkoutsBySubdomain();
    }
  }

  onProgramScrollEnd() {
    if (this.programPageNumber * this.pageSize === this.programData.length) {
      this.programPageNumber += 1;
      this.getProgramsByPartnerId();
    }
  }

  private async getPartner() {
    this.partnerState$.pipe(take(1)).subscribe((partner: Partner | null) => {
      if (partner) {
        this.partner = partner;
        this.setMetatag();
      }
    });
  }

  private async getWorkoutsBySubdomain() {
    const subdomain = this.subdomain.getSubdomain();
    this.store.dispatch(new GetWorkoutsBySubdomainWithPaging(subdomain, this.workoutPageNumber)).toPromise();
    this.workout$.pipe(takeUntil(this.subscription$)).subscribe(workoutData => {
        this.workoutData = _.orderBy(workoutData, ['orderNumber'], ['asc']);
      });
  }

  private async getProgramsByPartnerId() {
    this.store.dispatch(new GetProgramsByPartnerIdWithPaging(this.partner.id!, this.programPageNumber, this.pageSize));
    this.program$.pipe(takeUntil(this.subscription$)).subscribe(programData => {
          this.programData = _.orderBy(programData, ['orderNumber'], ['asc']);
        });
  }

  private setMetatag(): void {
    const subdomain: string = this.subdomain.getSubdomain();
    this.metaTagService.setTags(<Metadata>{
      site: subdomain,
      title: `${this.partner.name}'s Workout list`,
      description: 'Collection of workouts',
      url: `https://${subdomain}.fitscovery.com/workouts/list`,
      imageUrl: this.partner.bannerImage
    });
  }
}
